import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  ComputerDesktopIcon,
  CloudIcon,
  WifiIcon,
  PencilSquareIcon,
  EyeIcon,
  PrinterIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  BoltIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { WebPageSchema, BreadcrumbSchema } from '../../components/SEO/StructuredData';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DigitalConsentDentists() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const workflowRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'Digital Consent for Dentists | Paperless Dental Consent | Smile Consent',
    'Digital consent for dentists with paperless forms, digital signatures, patient-friendly workflows, and clear audit trails for UK practices.',
    'https://smileconsent.co.uk/digital-consent-dentists'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const digitalFeatures = [
    {
      icon: ClockIcon,
      title: 'Valid Consent in Minutes',
      description: 'Complete the entire consent process in just 5-10 minutes instead of 45+ minutes with paper forms.',
      benefits: [
        'Pre-filled patient information',
        'Smart form validation',
        'Instant error checking',
        'One-click completion'
      ],
      timeSaved: '35+ minutes per patient'
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Mobile-First Design',
      description: 'Patients can complete consent forms on any device - smartphone, tablet, or computer.',
      benefits: [
        'Responsive design',
        'Touch-friendly interface',
        'Offline capability',
        'Cross-platform compatibility'
      ],
      timeSaved: 'No device limitations'
    },
    {
      icon: CloudIcon,
      title: 'Cloud-Based Storage',
      description: 'Secure cloud storage with instant access from anywhere in your practice.',
      benefits: [
        'Automatic backups',
        'Version control',
        'Instant retrieval',
        'Secure encryption'
      ],
      timeSaved: 'No filing time'
    },
    {
      icon: ShareIcon,
      title: 'Instant Sharing',
      description: 'Share consent forms instantly with patients, colleagues, and specialists.',
      benefits: [
        'Email delivery',
        'SMS notifications',
        'Secure links',
        'Real-time updates'
      ],
      timeSaved: 'No printing or posting'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Real-Time Communication',
      description: 'Built-in messaging system for questions and clarifications during consent process.',
      benefits: [
        'Instant messaging',
        'Video call integration',
        'Document annotation',
        'Question tracking'
      ],
      timeSaved: 'No phone tag'
    },
    {
      icon: ChartBarIcon,
      title: 'Analytics & Insights',
      description: 'Track consent completion rates, common questions, and practice efficiency metrics.',
      benefits: [
        'Completion tracking',
        'Time analysis',
        'Question patterns',
        'Performance metrics'
      ],
      timeSaved: 'Data-driven improvements'
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Patient Arrival',
      description: 'Patient arrives at practice and checks in',
      traditional: '45+ minutes',
      digital: '2 minutes',
      icon: UserGroupIcon
    },
    {
      step: 2,
      title: 'Form Distribution',
      description: 'Hand out paper forms or send digital link',
      traditional: '5 minutes',
      digital: '30 seconds',
      icon: DocumentTextIcon
    },
    {
      step: 3,
      title: 'Form Completion',
      description: 'Patient fills out consent form',
      traditional: '25 minutes',
      digital: '8 minutes',
      icon: PencilSquareIcon
    },
    {
      step: 4,
      title: 'Review & Questions',
      description: 'Dentist reviews form and answers questions',
      traditional: '15 minutes',
      digital: '5 minutes',
      icon: EyeIcon
    },
    {
      step: 5,
      title: 'Signatures',
      description: 'Patient and dentist sign consent form',
      traditional: '5 minutes',
      digital: '2 minutes',
      icon: PencilSquareIcon
    },
    {
      step: 6,
      title: 'Filing & Storage',
      description: 'File paper form or store digitally',
      traditional: '5 minutes',
      digital: 'Automatic',
      icon: PrinterIcon
    }
  ];

  const digitalStats = [
    {
      metric: '35+ min',
      description: 'Average time saved per patient',
      icon: '⏰'
    },
    {
      metric: '300%',
      description: 'Efficiency improvement',
      icon: '🚀'
    },
    {
      metric: '95%',
      description: 'Patient satisfaction rate',
      icon: '😊'
    }
  ];

  const digitalBenefits = [
    {
      icon: RocketLaunchIcon,
      title: 'Increased Efficiency',
      description: 'Process 3x more patients with the same staff and time',
      metric: '300% efficiency gain'
    },
    {
      icon: LightBulbIcon,
      title: 'Better Patient Experience',
      description: 'Modern, intuitive interface that patients actually enjoy using',
      metric: '95% patient satisfaction'
    },
    {
      icon: BoltIcon,
      title: 'Reduced Errors',
      description: 'Smart validation prevents common mistakes and omissions',
      metric: '80% fewer errors'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enhanced Security',
      description: 'Encrypted digital storage with audit trails and compliance',
      metric: '100% secure'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Digital Consent for Dentists | Paperless Dental Consent | Smile Consent</title>
        <meta name="description" content="Digital consent for dentists with paperless forms, digital signatures, patient-friendly workflows, and clear audit trails for UK practices." />
        <meta name="keywords" content="digital consent for dentists, paperless dental consent, electronic dental consent, online dental consent forms, digital dental consent" />
        <link rel="canonical" href="https://smileconsent.co.uk/digital-consent-dentists" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Digital Consent for Dentists | Paperless Dental Consent | Smile Consent" />
        <meta property="og:description" content="Digital consent for dentists with paperless forms, digital signatures, and clear audit trails for UK practices." />
        <meta property="og:url" content="https://smileconsent.co.uk/digital-consent-dentists" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Digital Consent for Dentists | Paperless Dental Consent | Smile Consent" />
        <meta name="twitter:description" content="Digital consent for dentists with paperless forms, digital signatures, and clear audit trails for UK practices." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(WebPageSchema({
            name: "Digital Consent for Dentists",
            url: "https://smileconsent.co.uk/digital-consent-dentists",
            description: "Paperless digital consent for dentists with digital signatures for UK dental practices"
          }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(BreadcrumbSchema({
            items: [{ label: 'Digital Consent Forms', path: '/digital-consent-dentists' }]
          }))}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Breadcrumb items={[
            { label: 'Digital Consent Forms', path: '/digital-consent-dentists' }
          ]} />
        </div>

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-cyan-100 via-blue-100 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Digital Consent for Dentists
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              <span className="text-blue-600 font-bold">Paperless consent for modern dental practices.</span>
              Replace paper forms with digital consent workflows, patient-friendly signing, and audit-ready records for UK dentists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <RocketLaunchIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Transformation Banner */}
      <section className="bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ComputerDesktopIcon className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Full Consent, Less Stress</h2>
            </div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              Complete digital transformation for your consent process. 
              Modern, efficient, and designed for the digital age.
            </p>
          </div>
        </div>
      </section>

      {/* Digital Features Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Digital Consent Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to modernize your consent process and improve patient experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {digitalFeatures.map((feature, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="space-y-2 mb-4">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-green-800 text-center">
                    ⏱️ {feature.timeSaved}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Comparison Section */}
      <section ref={workflowRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Traditional vs Digital Workflow
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how digital consent transforms your practice workflow and saves time.
            </p>
          </div>

          <div className="space-y-8">
            {workflowSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg animate-on-scroll">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <step.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Step {step.step}: {step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Traditional Process</h4>
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-red-600" />
                      <span className="text-lg font-bold text-red-600">{step.traditional}</span>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Digital Process</h4>
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-green-600" />
                      <span className="text-lg font-bold text-green-600">{step.digital}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-600 text-white rounded-2xl p-8 text-center animate-on-scroll">
            <h3 className="text-2xl font-bold mb-4">Total Time Saved: 35+ Minutes Per Patient</h3>
            <p className="text-lg text-blue-100 mb-6">
              With digital consent, you can process 3x more patients with the same staff and time.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/20 p-4 rounded-lg">
                <div className="text-3xl font-bold">45+ min</div>
                <div className="text-sm">Traditional</div>
              </div>
              <div className="bg-white/20 p-4 rounded-lg">
                <div className="text-3xl font-bold">10 min</div>
                <div className="text-sm">Digital</div>
              </div>
              <div className="bg-white/20 p-4 rounded-lg">
                <div className="text-3xl font-bold">35+ min</div>
                <div className="text-sm">Time Saved</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Go Digital?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Digital consent management delivers measurable benefits for your practice and patients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {digitalBenefits.map((benefit, index) => (
              <div key={index} className="text-center animate-on-scroll">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 mb-4">{benefit.description}</p>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm font-bold text-green-800">{benefit.metric}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Stats Section */}
      <section ref={testimonialsRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Digital Transformation Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Measurable improvements from digital consent management implementation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {digitalStats.map((stat, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg animate-on-scroll text-center">
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.metric}</div>
                <p className="text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of dental practices already using digital consent management. 
            Start your free trial today and see the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-forms"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <DocumentTextIcon className="h-6 w-6" />
              View Templates
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Internal Navigation Section for SEO */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InternalLinks 
            title="Related Dental Practice Solutions"
            links={[
              {
                title: "Consent Templates",
                description: "AI-generated dental consent forms",
                path: "/dental-templates",
                icon: <ClipboardDocumentListIcon className="w-6 h-6" />
              },
              {
                title: "Pricing Plans",
                description: "Affordable plans for all practice sizes",
                path: "/dental-consent-plans",
                icon: <ChartBarIcon className="w-6 h-6" />
              },
              {
                title: "Compliance Software",
                description: "GDC & GDPR compliant solutions",
                path: "/dental-compliance-software",
                icon: <ShieldCheckIcon className="w-6 h-6" />
              },
              {
                title: "Practice Management",
                description: "Complete practice management system",
                path: "/dental-practice-management",
                icon: <BuildingOfficeIcon className="w-6 h-6" />
              },
              {
                title: "Wisdom tooth consent form UK",
                description: "Surgical extraction consent with digital workflow",
                path: "/wisdom-tooth-consent-form-uk",
                icon: <DocumentTextIcon className="w-6 h-6" />
              },
              {
                title: "Sedation consent form UK",
                description: "Digital sedation consent with recovery guidance",
                path: "/sedation-consent-form-uk",
                icon: <ClipboardDocumentListIcon className="w-6 h-6" />
              }
            ]}
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                Digital consent management for modern dental practices. Valid consent in minutes, not hours.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}